import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { toBackendUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";

const PRESETS_STORAGE_PREFIX = "scanmirein.location-presets.";

const blankForm = {
  name: "",
  description: "",
  stampCount: 10,
  reward: "",
  cooldownHours: "",
  resetAllowed: true,
  active: true,
  listedOnLandingPage: false,
  listedInCustomerDashboard: true,
  locationIds: [],
};

function normalizeCooldownInput(v) {
  const raw = String(v ?? "").trim();
  if (!raw) return null;
  const num = Number(raw.replace(/,/g, "."));
  if (!Number.isFinite(num) || num < 0) throw new Error("Cooldown muss eine positive Zahl sein");
  return Math.round(num * 100) / 100;
}

function toFormFromItem(item) {
  return {
    name: item?.name || "",
    description: item?.description || "",
    stampCount: Number(item?.stampCount || 1),
    reward: item?.reward || "",
    cooldownHours: item?.cooldownHours != null ? String(item.cooldownHours).replace(".", ",") : "",
    resetAllowed: !!item?.resetAllowed,
    active: !!item?.active,
    listedOnLandingPage: !!item?.listedOnLandingPage,
    listedInCustomerDashboard: !!item?.listedInCustomerDashboard,
    locationIds: Array.isArray(item?.locationIds) ? item.locationIds : [],
  };
}

function getPresetStorageKey(companyId) {
  return `${PRESETS_STORAGE_PREFIX}${companyId || "default"}`;
}

function loadSavedPresets(companyId) {
  try {
    const raw = localStorage.getItem(getPresetStorageKey(companyId));
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((preset) => ({
        id: String(preset?.id || ""),
        name: String(preset?.name || "").trim(),
        locationIds: Array.isArray(preset?.locationIds)
          ? Array.from(new Set(preset.locationIds.filter(Boolean)))
          : [],
      }))
      .filter((preset) => preset.id && preset.name && preset.locationIds.length > 0)
      .slice(0, 20);
  } catch (_) {
    return [];
  }
}

function persistSavedPresets(companyId, presets) {
  localStorage.setItem(getPresetStorageKey(companyId), JSON.stringify(presets.slice(0, 20)));
}

const StampCardsPage = () => {
  const { company, token } = useAuth();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [historyLocationPresets, setHistoryLocationPresets] = useState([]);
  const [savedLocationPresets, setSavedLocationPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [createForm, setCreateForm] = useState(blankForm);
  const [createThumbnailFile, setCreateThumbnailFile] = useState(null);
  const [createPresetName, setCreatePresetName] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(blankForm);
  const [editThumbnailFile, setEditThumbnailFile] = useState(null);
  const [editPresetName, setEditPresetName] = useState("");

  const locationPresets = useMemo(() => {
    const dedup = new Map();

    for (const preset of savedLocationPresets) {
      const ids = Array.isArray(preset?.locationIds)
        ? Array.from(new Set(preset.locationIds.filter(Boolean)))
        : [];
      const key = ids.sort().join("|");
      if (!key || dedup.has(key)) continue;
      dedup.set(key, {
        key: `saved:${preset.id}`,
        source: "Gespeichert",
        label: preset.name,
        names: ids
          .map((id) => locations.find((loc) => loc.id === id)?.name || id)
          .filter(Boolean),
        locationIds: ids,
      });
    }

    for (const preset of historyLocationPresets) {
      const ids = Array.isArray(preset?.locationIds)
        ? Array.from(new Set(preset.locationIds.filter(Boolean)))
        : [];
      const key = ids.sort().join("|");
      if (!key || dedup.has(key)) continue;
      dedup.set(key, {
        ...preset,
        key: preset.key || `history:${key}`,
      });
    }

    return Array.from(dedup.values()).slice(0, 16);
  }, [savedLocationPresets, historyLocationPresets, locations]);

  const editingItem = useMemo(
    () => (items || []).find((x) => x.id === editingId) || null,
    [items, editingId]
  );

  const load = async () => {
    try {
      setLoading(true);
      const [cardsResp, locationsResp, actionsResp] = await Promise.all([
        api.stampCardsGet(),
        api.getLocations(),
        token ? api.listActions(token) : Promise.resolve([]),
      ]);
      const cards = Array.isArray(cardsResp?.stampCards) ? cardsResp.stampCards : [];
      const allLocs = Array.isArray(locationsResp)
        ? locationsResp
        : (Array.isArray(locationsResp?.locations) ? locationsResp.locations : []);
      setItems(cards);
      setLocations(allLocs);

      const locationNameById = new Map((allLocs || []).map((loc) => [loc.id, loc.name]));
      const presets = [];

      const stampCardsRecent = cards.slice(0, 10);
      for (const card of stampCardsRecent) {
        const ids = Array.isArray(card?.locationIds)
          ? Array.from(new Set(card.locationIds.filter(Boolean)))
          : [];
        if (!ids.length) continue;
        presets.push({
          source: "Stempelkarte",
          label: card?.name || "Stempelkarte",
          locationIds: ids,
          names: ids.map((id) => locationNameById.get(id) || id),
        });
      }

      const actions = Array.isArray(actionsResp) ? actionsResp.slice(0, 10) : [];
      const actionLocations = await Promise.all(
        actions.map(async (action) => {
          try {
            const resp = await api.getActionLocations(action.id);
            const ids = Array.isArray(resp?.locations)
              ? Array.from(new Set(resp.locations.map((l) => l.id).filter(Boolean)))
              : [];
            return {
              action,
              ids,
              names: Array.isArray(resp?.locations)
                ? resp.locations.map((l) => l.name)
                : [],
            };
          } catch (_) {
            return { action, ids: [], names: [] };
          }
        })
      );

      for (const row of actionLocations) {
        if (!row.ids.length) continue;
        presets.push({
          source: "Aktion",
          label: row?.action?.title || "Aktion",
          locationIds: row.ids,
          names: row.names?.length ? row.names : row.ids.map((id) => locationNameById.get(id) || id),
        });
      }

      const dedup = new Map();
      for (const preset of presets) {
        const key = [...preset.locationIds].sort().join("|");
        if (!key || dedup.has(key)) continue;
        dedup.set(key, { ...preset, key });
      }
      setHistoryLocationPresets(Array.from(dedup.values()).slice(0, 12));
      setSavedLocationPresets(loadSavedPresets(company?.id));
    } catch (e) {
      toast({ title: "Fehler", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [company?.id, token]);

  const applyLocationToggle = (setter, currentIds, locationId, checked) => {
    const next = checked
      ? Array.from(new Set([...(currentIds || []), locationId]))
      : (currentIds || []).filter((id) => id !== locationId);
    setter((p) => ({ ...p, locationIds: next }));
  };

  const applyPreset = (setter, preset) => {
    const validIds = Array.isArray(preset?.locationIds)
      ? preset.locationIds.filter((id) => locations.some((loc) => loc.id === id))
      : [];
    setter((p) => ({ ...p, locationIds: Array.from(new Set(validIds)) }));
    if (Array.isArray(preset?.locationIds) && preset.locationIds.length > 0 && validIds.length === 0) {
      toast({ title: "Dieses Preset enthält keine aktuell verfügbaren Shops", variant: "destructive" });
    }
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setCreateStep(1);
  };

  const saveNamedPreset = (name, locationIds, clearName) => {
    const presetName = String(name || "").trim();
    const ids = Array.isArray(locationIds)
      ? Array.from(new Set(locationIds.filter(Boolean)))
      : [];

    if (!presetName) {
      toast({ title: "Bitte Preset-Namen eingeben", variant: "destructive" });
      return;
    }
    if (ids.length === 0) {
      toast({ title: "Bitte mindestens einen Shop auswählen", variant: "destructive" });
      return;
    }

    const next = [
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: presetName,
        locationIds: ids,
      },
      ...savedLocationPresets,
    ].slice(0, 20);

    setSavedLocationPresets(next);
    persistSavedPresets(company?.id, next);
    clearName("");
    toast({ title: "Preset gespeichert" });
  };

  const createCard = async (e) => {
    e?.preventDefault?.();
    if (!createForm.name.trim() || !createForm.reward.trim()) {
      toast({ title: "Bitte Name und Belohnung ausfüllen", variant: "destructive" });
      return;
    }

    try {
      setSaving(true);
      const cooldown = normalizeCooldownInput(createForm.cooldownHours);
      const created = await api.stampCardCreate({
        name: createForm.name.trim(),
        description: createForm.description.trim() || null,
        stampCount: Number(createForm.stampCount) || 1,
        reward: createForm.reward.trim(),
        cooldownHours: cooldown,
        resetAllowed: !!createForm.resetAllowed,
        active: !!createForm.active,
        listedOnLandingPage: !!createForm.listedOnLandingPage,
        listedInCustomerDashboard: !!createForm.listedInCustomerDashboard,
        locationIds: createForm.locationIds || [],
      });

      const cardId = created?.stampCard?.id;
      if (cardId && createThumbnailFile) {
        await api.stampCardUploadThumbnail(cardId, createThumbnailFile);
      }

      setCreateForm(blankForm);
      setCreateThumbnailFile(null);
      setCreatePresetName("");
      closeCreateModal();
      toast({ title: "Stempelkarte erstellt" });
      await load();
    } catch (e) {
      toast({ title: "Fehler", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm(toFormFromItem(item));
    setEditThumbnailFile(null);
    setEditPresetName("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(blankForm);
    setEditThumbnailFile(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      const cooldown = normalizeCooldownInput(editForm.cooldownHours);
      await api.stampCardUpdate(editingId, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        stampCount: Number(editForm.stampCount) || 1,
        reward: editForm.reward.trim(),
        cooldownHours: cooldown,
        resetAllowed: !!editForm.resetAllowed,
        active: !!editForm.active,
        listedOnLandingPage: !!editForm.listedOnLandingPage,
        listedInCustomerDashboard: !!editForm.listedInCustomerDashboard,
        locationIds: editForm.locationIds || [],
      });

      if (editThumbnailFile) {
        await api.stampCardUploadThumbnail(editingId, editThumbnailFile);
      }

      toast({ title: "Stempelkarte gespeichert" });
      cancelEdit();
      await load();
    } catch (e) {
      toast({ title: "Fehler", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const removeCard = async (item) => {
    try {
      await api.stampCardDelete(item.id);
      toast({ title: "Stempelkarte gelöscht" });
      if (editingId === item.id) cancelEdit();
      await load();
    } catch (e) {
      toast({ title: "Fehler", description: e.message, variant: "destructive" });
    }
  };

  if (!company?.stampCardsEnabled) {
    return (
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Stempelkarten</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              StempelMirEin ist für diese Organisation aktuell nicht aktiviert.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Stempelkarten</CardTitle>
            <Button type="button" onClick={() => { setCreateOpen(true); setCreateStep(1); }}>
              Neue Stempelkarte
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Neue Stempelkarten werden im Schritt-für-Schritt Popup erstellt.
          </div>
        </CardContent>
      </Card>

      {createOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeCreateModal}>
          <form
            className="bg-background rounded shadow w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
            onSubmit={createCard}
          >
            <div className="flex items-center justify-between gap-3">
              <h4 className="font-semibold text-sm">Neue Stempelkarte</h4>
              <div className="text-[11px] text-muted-foreground">Schritt {createStep} von 4</div>
            </div>

            <ol className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]" aria-label="Schritte">
              {[
                { n: 1, label: "Grunddaten" },
                { n: 2, label: "Regeln" },
                { n: 3, label: "Sichtbarkeit & Shops" },
                { n: 4, label: "Thumbnail & Abschluss" },
              ].map((step) => (
                <li
                  key={step.n}
                  className={`px-2 py-1 rounded border ${createStep === step.n ? "bg-blue-600 text-white border-blue-600" : "bg-muted text-foreground border-border"}`}
                >
                  {step.n}. {step.label}
                </li>
              ))}
            </ol>

            {createStep === 1 && (
              <div className="space-y-3 text-sm">
                <div className="text-muted-foreground text-xs">Name, Belohnung und Beschreibung festlegen.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    className="border rounded px-3 py-2 text-sm"
                    placeholder="Titel"
                    value={createForm.name}
                    onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                    required
                  />
                  <input
                    className="border rounded px-3 py-2 text-sm"
                    placeholder="Belohnung"
                    value={createForm.reward}
                    onChange={(e) => setCreateForm((p) => ({ ...p, reward: e.target.value }))}
                    required
                  />
                </div>
                <textarea
                  className="border rounded px-3 py-2 text-sm w-full"
                  rows={3}
                  placeholder="Beschreibung"
                  value={createForm.description}
                  onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
            )}

            {createStep === 2 && (
              <div className="space-y-3 text-sm">
                <div className="text-muted-foreground text-xs">Stempelregeln und Status einstellen.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="border rounded px-3 py-2 text-sm"
                    placeholder="Anzahl Stempel"
                    value={createForm.stampCount}
                    onChange={(e) => setCreateForm((p) => ({ ...p, stampCount: Number(e.target.value) || 1 }))}
                  />
                  <input
                    className="border rounded px-3 py-2 text-sm"
                    placeholder="Cooldown in Stunden (z. B. 24 oder 0,5)"
                    value={createForm.cooldownHours}
                    onChange={(e) => setCreateForm((p) => ({ ...p, cooldownHours: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={createForm.resetAllowed}
                      onChange={(e) => setCreateForm((p) => ({ ...p, resetAllowed: e.target.checked }))}
                    />
                    Wiederholtes Verwenden erlauben
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={createForm.active}
                      onChange={(e) => setCreateForm((p) => ({ ...p, active: e.target.checked }))}
                    />
                    Aktiv
                  </label>
                </div>
              </div>
            )}

            {createStep === 3 && (
              <div className="space-y-3 text-sm">
                <div className="text-muted-foreground text-xs">Sichtbarkeit und teilnehmende Shops auswählen.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={createForm.listedInCustomerDashboard}
                      onChange={(e) => setCreateForm((p) => ({ ...p, listedInCustomerDashboard: e.target.checked }))}
                    />
                    Im User-Dashboard listen
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={createForm.listedOnLandingPage}
                      onChange={(e) => setCreateForm((p) => ({ ...p, listedOnLandingPage: e.target.checked }))}
                    />
                    Auf Landingpage listen
                  </label>
                </div>

                <div>
                  <div className="text-xs text-muted-foreground mb-1">Teilnehmende Shops</div>
                  <div className="mb-2 flex gap-2">
                    <input
                      className="border rounded px-3 py-2 text-sm flex-1"
                      placeholder="Preset-Name (z. B. Innenstadt)"
                      value={createPresetName}
                      onChange={(e) => setCreatePresetName(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => saveNamedPreset(createPresetName, createForm.locationIds, setCreatePresetName)}
                    >
                      Auswahl speichern
                    </Button>
                  </div>
                  {locationPresets.length > 0 && (
                    <div className="mb-2">
                      <div className="text-[11px] text-muted-foreground mb-1">Verlauf / Schnellvorlagen</div>
                      <div className="flex flex-wrap gap-2">
                        {locationPresets.map((preset) => (
                          <button
                            key={preset.key}
                            type="button"
                            className="text-xs border rounded px-2 py-1 hover:bg-muted"
                            onClick={() => applyPreset(setCreateForm, preset)}
                            title={preset.names.join(", ")}
                          >
                            {preset.source}: {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {locations.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Keine Shops vorhanden</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm max-h-56 overflow-y-auto border rounded p-2">
                      {locations.map((loc) => (
                        <label key={loc.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={createForm.locationIds.includes(loc.id)}
                            onChange={(e) =>
                              applyLocationToggle(
                                setCreateForm,
                                createForm.locationIds,
                                loc.id,
                                e.target.checked
                              )
                            }
                          />
                          {loc.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {createStep === 4 && (
              <div className="space-y-3 text-sm">
                <div className="text-muted-foreground text-xs">Optional Thumbnail hochladen und dann erstellen.</div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Thumbnail</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="text-sm"
                    onChange={(e) => setCreateThumbnailFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="text-xs text-muted-foreground border rounded p-3 bg-muted">
                  <div><strong>Titel:</strong> {createForm.name || "-"}</div>
                  <div><strong>Belohnung:</strong> {createForm.reward || "-"}</div>
                  <div><strong>Stempel:</strong> {createForm.stampCount}</div>
                  <div><strong>Cooldown:</strong> {createForm.cooldownHours?.trim() ? `${createForm.cooldownHours} h` : "kein Cooldown"}</div>
                  <div><strong>Shops:</strong> {createForm.locationIds.length}</div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t">
              <Button type="button" variant="outline" onClick={closeCreateModal}>Abbrechen</Button>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={createStep === 1}
                  onClick={() => setCreateStep((s) => Math.max(1, s - 1))}
                >
                  Zurück
                </Button>
                {createStep < 4 ? (
                  <Button
                    type="button"
                    onClick={() => {
                      if (createStep === 1 && (!createForm.name.trim() || !createForm.reward.trim())) {
                        toast({ title: "Bitte Name und Belohnung ausfüllen", variant: "destructive" });
                        return;
                      }
                      setCreateStep((s) => Math.min(4, s + 1));
                    }}
                  >
                    Weiter
                  </Button>
                ) : (
                  <Button type="submit" disabled={saving}>{saving ? "..." : "Erstellen"}</Button>
                )}
              </div>
            </div>
          </form>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Bestehende Stempelkarten</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Lade...</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground">Noch keine Stempelkarten vorhanden.</div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => {
                return (
                  <div key={item.id} className="border rounded p-3 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{item.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {item.stampCount} Stempel · Belohnung: {item.reward}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          Cooldown: {item.cooldownHours != null ? `${String(item.cooldownHours).replace(".", ",")} h` : "kein Cooldown"}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          User-Dashboard: {item.listedInCustomerDashboard ? "gelistet" : "nicht gelistet"} · Landingpage: {item.listedOnLandingPage ? "gelistet" : "nicht gelistet"}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          Shops: {Array.isArray(item.locations) && item.locations.length > 0 ? item.locations.map((x) => x.name).join(", ") : "keine"}
                        </div>
                        {item.qrCode && <div className="text-[11px] text-muted-foreground mt-1">QR: {item.qrCode}</div>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => startEdit(item)}>Bearbeiten</Button>
                        <Button size="sm" variant="destructive" onClick={() => removeCard(item)}>Löschen</Button>
                      </div>
                    </div>
                    {item.thumbnailUrl && (
                      <img
                        src={toBackendUrl(item.thumbnailUrl)}
                        alt={item.name}
                        className="w-24 h-24 rounded object-cover border"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {editingId && editingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={cancelEdit}>
          <div className="bg-background rounded shadow w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3">
              <h4 className="font-semibold text-sm">Stempelkarte bearbeiten</h4>
              <Button type="button" size="sm" variant="outline" onClick={cancelEdit}>Schließen</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                className="border rounded px-3 py-2 text-sm"
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              />
              <input
                className="border rounded px-3 py-2 text-sm"
                value={editForm.reward}
                onChange={(e) => setEditForm((p) => ({ ...p, reward: e.target.value }))}
              />
              <input
                type="number"
                min={1}
                max={100}
                className="border rounded px-3 py-2 text-sm"
                value={editForm.stampCount}
                onChange={(e) => setEditForm((p) => ({ ...p, stampCount: Number(e.target.value) || 1 }))}
              />
              <input
                className="border rounded px-3 py-2 text-sm"
                placeholder="Cooldown in Stunden (leer = keiner)"
                value={editForm.cooldownHours}
                onChange={(e) => setEditForm((p) => ({ ...p, cooldownHours: e.target.value }))}
              />
            </div>

            <textarea
              className="border rounded px-3 py-2 text-sm w-full"
              rows={2}
              value={editForm.description}
              onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
            />

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Thumbnail ersetzen</label>
              {editingItem?.thumbnailUrl && (
                <img
                  src={toBackendUrl(editingItem.thumbnailUrl)}
                  alt={editingItem.name}
                  className="w-24 h-24 rounded object-cover border mb-2"
                />
              )}
              <input
                type="file"
                accept="image/*"
                className="text-sm"
                onChange={(e) => setEditThumbnailFile(e.target.files?.[0] || null)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.resetAllowed}
                  onChange={(e) => setEditForm((p) => ({ ...p, resetAllowed: e.target.checked }))}
                />
                Wiederholtes Verwenden erlauben
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.active}
                  onChange={(e) => setEditForm((p) => ({ ...p, active: e.target.checked }))}
                />
                Aktiv
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.listedInCustomerDashboard}
                  onChange={(e) => setEditForm((p) => ({ ...p, listedInCustomerDashboard: e.target.checked }))}
                />
                Im User-Dashboard listen
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.listedOnLandingPage}
                  onChange={(e) => setEditForm((p) => ({ ...p, listedOnLandingPage: e.target.checked }))}
                />
                Auf Landingpage listen
              </label>
            </div>

            <div>
              <div className="text-xs text-muted-foreground mb-1">Teilnehmende Shops</div>
              <div className="mb-2 flex gap-2">
                <input
                  className="border rounded px-3 py-2 text-sm flex-1"
                  placeholder="Preset-Name (z. B. Innenstadt)"
                  value={editPresetName}
                  onChange={(e) => setEditPresetName(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => saveNamedPreset(editPresetName, editForm.locationIds, setEditPresetName)}
                >
                  Auswahl speichern
                </Button>
              </div>
              {locationPresets.length > 0 && (
                <div className="mb-2">
                  <div className="text-[11px] text-muted-foreground mb-1">Verlauf / Schnellvorlagen</div>
                  <div className="flex flex-wrap gap-2">
                    {locationPresets.map((preset) => (
                      <Button
                        key={preset.key}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={() => applyPreset(setEditForm, preset)}
                        title={preset.names.join(", ")}
                      >
                        {preset.source}: {preset.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {locations.length === 0 ? (
                <div className="text-xs text-muted-foreground">Keine Shops vorhanden</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm max-h-56 overflow-y-auto border rounded p-2">
                  {locations.map((loc) => (
                    <label key={loc.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editForm.locationIds.includes(loc.id)}
                        onChange={(e) =>
                          applyLocationToggle(
                            setEditForm,
                            editForm.locationIds,
                            loc.id,
                            e.target.checked
                          )
                        }
                      />
                      {loc.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={cancelEdit}>Abbrechen</Button>
              <Button size="sm" onClick={saveEdit} disabled={saving}>{saving ? "..." : "Speichern"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StampCardsPage;
